import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase, DbCampaign } from '../lib/supabase';
import type { CampaignProfile, SoloCampaignAIConfig } from '@machina/core';
import { createSubscription } from '../lib/realtimeHelpers';

/**
 * Campaign Store
 *
 * Manages campaign state and Supabase sync.
 * Campaigns are the top-level container for all game data.
 */

export type MapMode = 'campaign' | 'battle';

export interface CampaignSettings {
  profile?: CampaignProfile;
  currentMapMode?: MapMode;
  [key: string]: unknown;
}

export interface Campaign {
  id: string;
  ownerId: string | null;
  name: string;
  description: string | null;
  settings: CampaignSettings;
  /** Top-level playset identifier (e.g. 'tethered_city', 'standard_dnd') */
  playset: string | null;
  campaignType: 'standard' | 'solo' | 'ai_dm';
  aiConfig: SoloCampaignAIConfig | null;
  role: 'dm' | 'player';
  createdAt: string;
  updatedAt: string;
}

interface CampaignState {
  // Data
  currentCampaignId: string | null;
  campaigns: Campaign[];
  isLoading: boolean;
  error: string | null;
  realtimeCleanup: (() => void) | null;

  // Actions
  setCurrentCampaign: (id: string | null) => void;
  loadCampaigns: (userId: string) => Promise<void>;
  createCampaign: (userId: string, name: string, description?: string, profile?: CampaignProfile, campaignType?: 'standard' | 'solo' | 'ai_dm', aiConfig?: SoloCampaignAIConfig) => Promise<Campaign | null>;
  joinCampaign: (userId: string, campaignId: string) => Promise<boolean>;
  leaveCampaign: (userId: string, campaignId: string) => Promise<boolean>;
  updateCampaignSettings: (campaignId: string, settings: CampaignSettings) => Promise<void>;
  setMapMode: (campaignId: string, mapMode: MapMode) => Promise<void>;
  switchToSolo: (campaignId: string, aiConfig: SoloCampaignAIConfig) => Promise<boolean>;
  switchToStandard: (campaignId: string) => Promise<boolean>;

  // Realtime
  subscribeToRealtime: (campaignId: string) => void;
  unsubscribeFromRealtime: () => void;

  // Queries
  getCurrentCampaign: () => Campaign | null;
}

function dbToCampaign(db: DbCampaign, role: 'dm' | 'player'): Campaign {
  const raw = db as unknown as Record<string, unknown>;
  // Prefer top-level playset column, fall back to JSONB for un-migrated rows
  const playset = db.playset
    ?? (db.settings as Record<string, unknown>)?.profile
      ? ((db.settings as Record<string, unknown>).profile as Record<string, unknown>)?.playset as string | null ?? null
      : null;
  return {
    id: db.id,
    ownerId: db.owner_id,
    name: db.name,
    description: db.description,
    settings: db.settings || {},
    playset,
    campaignType: ((raw.campaign_type as string) || 'standard') as 'standard' | 'solo' | 'ai_dm',
    aiConfig: (raw.ai_config as SoloCampaignAIConfig) || null,
    role,
    createdAt: db.created_at,
    updatedAt: db.updated_at,
  };
}

export const useCampaignStore = create<CampaignState>()(
  persist(
    (set, get) => ({
      currentCampaignId: null,
      campaigns: [],
      isLoading: false,
      error: null,
      realtimeCleanup: null,

      setCurrentCampaign: (id) => {
        set({ currentCampaignId: id });
      },

      loadCampaigns: async (userId) => {
        set({ isLoading: true, error: null });
        try {
          // Get all campaign memberships for this user
          const { data: memberships, error: memberError } = await supabase
            .from('machina_campaign_members')
            .select('campaign_id, role')
            .eq('user_id', userId);

          if (memberError) throw memberError;
          if (!memberships || memberships.length === 0) {
            set({ campaigns: [], isLoading: false });
            return;
          }

          // Get campaign details
          const campaignIds = memberships.map((m) => m.campaign_id);
          const { data: campaigns, error: campaignError } = await supabase
            .from('machina_campaigns')
            .select('*')
            .in('id', campaignIds);

          if (campaignError) throw campaignError;

          // Map to Campaign objects with roles
          const roleMap = new Map(memberships.map((m) => [m.campaign_id, m.role as 'dm' | 'player']));
          const result = (campaigns || []).map((c) =>
            dbToCampaign(c as DbCampaign, roleMap.get(c.id) || 'player')
          );

          set({ campaigns: result, isLoading: false });
        } catch (err) {
          set({ error: (err as Error).message, isLoading: false });
        }
      },

      createCampaign: async (userId, name, description, profile, campaignType = 'standard', aiConfig) => {
        set({ isLoading: true, error: null });
        try {
          // Create the campaign with optional profile in settings
          const settings: CampaignSettings = profile ? { profile } : {};
          const insertData: Record<string, unknown> = {
            owner_id: userId,
            name,
            description: description || null,
            settings,
            playset: profile?.playset ?? null,
            campaign_type: campaignType,
          };
          if (aiConfig) {
            insertData.ai_config = aiConfig;
          }
          const { data: campaign, error: createError } = await supabase
            .from('machina_campaigns')
            .insert(insertData)
            .select()
            .single();

          if (createError) throw createError;

          // Add owner as DM member (solo players get DM role to trigger DM-scoped AI actions)
          const { error: memberError } = await supabase
            .from('machina_campaign_members')
            .insert({
              campaign_id: campaign.id,
              user_id: userId,
              role: 'dm',
            });

          if (memberError) throw memberError;

          const newCampaign = dbToCampaign(campaign as DbCampaign, 'dm');
          set((state) => ({
            campaigns: [...state.campaigns, newCampaign],
            currentCampaignId: newCampaign.id,
            isLoading: false,
          }));

          return newCampaign;
        } catch (err) {
          set({ error: (err as Error).message, isLoading: false });
          return null;
        }
      },

      joinCampaign: async (userId, campaignId) => {
        set({ isLoading: true, error: null });
        try {
          // Check if campaign exists
          const { data: campaign, error: campaignError } = await supabase
            .from('machina_campaigns')
            .select('*')
            .eq('id', campaignId)
            .single();

          if (campaignError) throw campaignError;

          // Block joining solo campaigns
          if ((campaign as Record<string, unknown>).campaign_type === 'solo') {
            throw new Error('Cannot join a solo campaign');
          }

          // Add as player member
          const { error: memberError } = await supabase
            .from('machina_campaign_members')
            .insert({
              campaign_id: campaignId,
              user_id: userId,
              role: 'player',
            });

          if (memberError) throw memberError;

          const newCampaign = dbToCampaign(campaign as DbCampaign, 'player');
          set((state) => ({
            campaigns: [...state.campaigns, newCampaign],
            currentCampaignId: newCampaign.id,
            isLoading: false,
          }));

          return true;
        } catch (err) {
          set({ error: (err as Error).message, isLoading: false });
          return false;
        }
      },

      leaveCampaign: async (userId, campaignId) => {
        set({ isLoading: true, error: null });
        try {
          const { error } = await supabase
            .from('machina_campaign_members')
            .delete()
            .eq('campaign_id', campaignId)
            .eq('user_id', userId);

          if (error) throw error;

          set((state) => ({
            campaigns: state.campaigns.filter((c) => c.id !== campaignId),
            currentCampaignId:
              state.currentCampaignId === campaignId ? null : state.currentCampaignId,
            isLoading: false,
          }));

          return true;
        } catch (err) {
          set({ error: (err as Error).message, isLoading: false });
          return false;
        }
      },

      updateCampaignSettings: async (campaignId, settings) => {
        try {
          const { error } = await supabase
            .from('machina_campaigns')
            .update({ settings, updated_at: new Date().toISOString() })
            .eq('id', campaignId);

          if (error) throw error;

          set((state) => ({
            campaigns: state.campaigns.map((c) =>
              c.id === campaignId ? { ...c, settings, updatedAt: new Date().toISOString() } : c
            ),
          }));
        } catch (err) {
          set({ error: (err as Error).message });
        }
      },

      setMapMode: async (campaignId, mapMode) => {
        const campaign = get().campaigns.find((c) => c.id === campaignId);
        if (!campaign) return;

        const newSettings: CampaignSettings = {
          ...campaign.settings,
          currentMapMode: mapMode,
        };

        console.log('[CampaignStore] Setting map mode to:', mapMode);

        try {
          const { error } = await supabase
            .from('machina_campaigns')
            .update({ settings: newSettings, updated_at: new Date().toISOString() })
            .eq('id', campaignId);

          if (error) throw error;

          // Local update will come from realtime subscription
        } catch (err) {
          console.error('[CampaignStore] Failed to set map mode:', err);
          set({ error: (err as Error).message });
        }
      },

      switchToSolo: async (campaignId, aiConfig) => {
        try {
          const { error } = await supabase
            .from('machina_campaigns')
            .update({
              campaign_type: 'solo',
              ai_config: aiConfig,
              updated_at: new Date().toISOString(),
            })
            .eq('id', campaignId);

          if (error) throw error;

          set((state) => ({
            campaigns: state.campaigns.map((c) =>
              c.id === campaignId ? { ...c, campaignType: 'solo' as const, aiConfig } : c
            ),
          }));
          return true;
        } catch (err) {
          console.error('[CampaignStore] Failed to switch to solo:', err);
          set({ error: (err as Error).message });
          return false;
        }
      },

      switchToStandard: async (campaignId) => {
        try {
          const { error } = await supabase
            .from('machina_campaigns')
            .update({
              campaign_type: 'standard',
              ai_config: null,
              updated_at: new Date().toISOString(),
            })
            .eq('id', campaignId);

          if (error) throw error;

          set((state) => ({
            campaigns: state.campaigns.map((c) =>
              c.id === campaignId ? { ...c, campaignType: 'standard' as const, aiConfig: null } : c
            ),
          }));
          return true;
        } catch (err) {
          console.error('[CampaignStore] Failed to switch to standard:', err);
          set({ error: (err as Error).message });
          return false;
        }
      },

      subscribeToRealtime: (campaignId) => {
        get().unsubscribeFromRealtime();

        const { cleanup } = createSubscription({
          channelName: `campaign:${campaignId}`,
          table: 'machina_campaigns',
          filter: `id=eq.${campaignId}`,
          event: 'UPDATE',
          onPayload: (payload) => {
            const updatedCampaign = payload.new as unknown as DbCampaign;
            set((state) => ({
              campaigns: state.campaigns.map((c) =>
                c.id === campaignId
                  ? {
                      ...c,
                      settings: updatedCampaign.settings || {},
                      updatedAt: updatedCampaign.updated_at,
                    }
                  : c
              ),
            }));
          },
        });

        set({ realtimeCleanup: cleanup });
      },

      unsubscribeFromRealtime: () => {
        const { realtimeCleanup } = get();
        if (realtimeCleanup) {
          realtimeCleanup();
          set({ realtimeCleanup: null });
        }
      },

      getCurrentCampaign: () => {
        const { campaigns, currentCampaignId } = get();
        return campaigns.find((c) => c.id === currentCampaignId) || null;
      },
    }),
    {
      name: 'machina-campaign',
      partialize: (state) => ({
        currentCampaignId: state.currentCampaignId,
        campaigns: state.campaigns, // Persist campaigns so they show immediately on load
      }),
    }
  )
);
